import { Link } from "react-router-dom";
import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useApp } from "@/context/AppContext";
import { UNCLAIMED as MOCK_UNCLAIMED, TAX_CREDITS, SETTLEMENTS, formatMoney, formatDeadline, daysUntil } from "@/lib/mockData";
import GrantCard from "@/components/common/GrantCard";
import MatchRing from "@/components/common/MatchRing";
import ShareBanner from "@/components/common/ShareBanner";
import DeadlineStrip from "@/components/common/DeadlineStrip";
import ApplicationsDeadlinesPanel from "@/components/common/ApplicationsDeadlinesPanel";
import { PillBadge } from "@/components/common/Bits";
import { ArrowUpRight, Sparkles, Wallet, Receipt, Landmark, CalendarDays, TrendingUp, CheckCircle2, FileText } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

export default function Dashboard() {
    const { user, profile, totals, scoredGrants, applications, unclaimed } = useApp();
    const topGrants = scoredGrants.slice(0, 3);
    const topClaims = (unclaimed.length ? unclaimed : MOCK_UNCLAIMED).slice(0, 3);

    const pieData = useMemo(() => [
        { name: "Grants",      value: totals.grants,      color: "hsl(var(--chart-1))" },
        { name: "Unclaimed",   value: totals.unclaimed,   color: "hsl(var(--chart-2))" },
        { name: "Tax credits", value: totals.credits,     color: "hsl(var(--chart-3))" },
        { name: "Settlements", value: totals.settlements, color: "hsl(var(--chart-4))" },
    ], [totals]);

    const firstName = (profile?.legal_first_name || user?.name || "Friend").split(" ")[0];

    return (
        <div className="px-5 sm:px-8 py-8 lg:py-10 max-w-7xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Welcome back</p>
                    <h1 className="mt-2 font-serif text-4xl sm:text-5xl leading-tight tracking-tight">Hello, {firstName}.</h1>
                </div>
                <PillBadge tone="success" data-testid="dashboard-scan-badge">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Live scan · {scoredGrants.length + topClaims.length + TAX_CREDITS.length + SETTLEMENTS.length} results
                </PillBadge>
            </div>

            <Card className="mt-6 relative overflow-hidden border-0 bg-gradient-primary text-primary-foreground shadow-elegant">
                <div aria-hidden className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-accent/20 blur-3xl" />
                <div aria-hidden className="absolute -bottom-20 -left-10 h-60 w-60 rounded-full bg-primary-glow/30 blur-3xl" />
                <div className="relative grid md:grid-cols-5 gap-8 p-8 sm:p-10">
                    <div className="md:col-span-3 flex flex-col justify-center">
                        <p className="text-xs font-mono uppercase tracking-[0.22em] text-primary-foreground/60">Total potential</p>
                        <p className="mt-3 font-serif text-6xl sm:text-7xl tracking-tight" data-testid="dashboard-total-potential">{formatMoney(totals.potential, { compact: true })}</p>
                        <p className="mt-3 text-sm text-primary-foreground/70 max-w-md leading-relaxed">
                            Across matched grants, unclaimed funds, missed credits, and open settlements. Start with the easy wins — Owed AI files the paperwork for you.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            <Button asChild size="sm" className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"><Link to="/app/claims">Claim easy wins</Link></Button>
                            <Button asChild size="sm" variant="outline" className="rounded-full bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground"><Link to="/app/grants">Browse grants</Link></Button>
                            <Button asChild size="sm" variant="outline" className="rounded-full bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground"><Link to="/app/live">Live Grants.gov</Link></Button>
                        </div>
                    </div>
                    <div className="md:col-span-2 relative h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={pieData} dataKey="value" innerRadius={55} outerRadius={90} paddingAngle={2} stroke="none">
                                    {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                </Pie>
                                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, color: "hsl(var(--foreground))" }} formatter={(v) => formatMoney(v)} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="text-center">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-primary-foreground/60">Mix</p>
                                <p className="font-serif text-xl">4 streams</p>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            <DeadlineStrip />

            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard icon={Sparkles} label="Matched grants"   value={formatMoney(totals.grants, { compact: true })} sub={`${scoredGrants.length} programs`} tone="primary" />
                <StatCard icon={Wallet}   label="Unclaimed"        value={formatMoney(totals.unclaimed)}                 sub={`${topClaims.length} records surfaced`} tone="accent" />
                <StatCard icon={Receipt}  label="Missed credits"   value={formatMoney(totals.credits)}                   sub={`${TAX_CREDITS.length} prior-year credits`} tone="terracotta" />
                <StatCard icon={Landmark} label="Open settlements" value={formatMoney(totals.settlements)}               sub={`${SETTLEMENTS.filter(s => s.status === "Open").length} active`} tone="primary" />
            </div>

            <div className="mt-10 grid lg:grid-cols-3 gap-6">
                <section className="lg:col-span-2">
                    <div className="flex items-end justify-between mb-5">
                        <div>
                            <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Top matches</p>
                            <h2 className="mt-1 font-serif text-2xl sm:text-3xl">Grants handpicked for you</h2>
                        </div>
                        <Button asChild variant="ghost" size="sm" className="rounded-full"><Link to="/app/grants">See all <ArrowUpRight className="h-4 w-4 ml-1" /></Link></Button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {topGrants.map((g) => <GrantCard key={g.id} grant={g} compact />)}
                    </div>
                </section>

                <aside className="space-y-6">
                    <Card className="p-6 border-border">
                        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Easy wins</p>
                        <h3 className="mt-1 font-serif text-2xl">Claim this week</h3>
                        <div className="mt-5 space-y-3">
                            {topClaims.map((c) => (
                                <Link to={`/app/detail/claim/${c.id}`} key={c.id} className="group flex items-start gap-3 p-3 -mx-3 rounded-xl hover:bg-secondary transition-colors">
                                    <span className="h-9 w-9 rounded-lg bg-accent/15 text-accent-foreground inline-flex items-center justify-center shrink-0">
                                        <Wallet className="h-4 w-4" />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{c.source}</p>
                                        <p className="text-xs text-muted-foreground truncate">{c.description}</p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="font-serif text-base">{formatMoney(c.amount)}</p>
                                        <p className="text-[10px] text-muted-foreground">{c.confidence}% match</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                        <Button asChild variant="outline" className="mt-5 w-full rounded-full border-border"><Link to="/app/claims">View all claims</Link></Button>
                    </Card>

                    <Card className="p-6 border-border relative overflow-hidden">
                        <div aria-hidden className="absolute -top-8 -right-8 h-24 w-24 rounded-full bg-accent/10 blur-xl" />
                        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Deadlines</p>
                        <h3 className="mt-1 font-serif text-2xl">On the horizon</h3>
                        <div className="mt-5 space-y-3">
                            {scoredGrants.filter(g => daysUntil(g.deadline) !== null).slice(0, 4).map(g => {
                                const d = daysUntil(g.deadline);
                                return (
                                    <div key={g.id} className="flex items-center gap-3">
                                        <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{g.title}</p>
                                            <p className="text-xs text-muted-foreground">{formatDeadline(g.deadline)}</p>
                                        </div>
                                        <Badge variant="outline" className={d <= 30 ? "bg-terracotta/10 border-terracotta/30 text-terracotta" : "bg-secondary border-border"}>{d}d</Badge>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                </aside>
            </div>

            <section className="mt-10" data-testid="dashboard-apps-section">
                <ApplicationsDeadlinesPanel />
            </section>

            <section className="mt-12">
                <Card className="p-8 sm:p-10 bg-gradient-warm border-0">
                    <div className="grid md:grid-cols-5 gap-8 items-center">
                        <div className="md:col-span-3">
                            <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Intuition</p>
                            <h3 className="mt-2 font-serif text-3xl sm:text-4xl leading-tight text-balance">Your fastest win: <span className="italic text-primary">missed EITC</span> from 2022.</h3>
                            <p className="mt-3 text-muted-foreground max-w-md leading-relaxed">Based on your filing signals, amending 2022 could return an estimated ~$3,400 in 4-6 weeks. Owed AI fills the whole 1040-X in minutes.</p>
                            <div className="mt-5 flex gap-2">
                                <Button asChild className="rounded-full bg-foreground text-background hover:bg-foreground/90"><Link to="/app/detail/credit/t-eitc">Start in 20 minutes</Link></Button>
                                <Button asChild variant="outline" className="rounded-full bg-card"><Link to="/app/claims">See all credits</Link></Button>
                            </div>
                        </div>
                        <div className="md:col-span-2 flex justify-center">
                            <MatchRing score={94} size={180} label="confidence" />
                        </div>
                    </div>
                </Card>
            </section>

            <ShareBanner total={totals.potential} />
        </div>
    );
}

function StatCard({ icon: Icon, label, value, sub, tone }) {
    const toneMap = {
        primary: "bg-primary text-primary-foreground",
        accent: "bg-accent text-accent-foreground",
        terracotta: "bg-terracotta text-primary-foreground",
    };
    return (
        <Card className="p-5 border-border flex flex-col gap-3 hover:shadow-soft transition-shadow">
            <div className="flex items-center justify-between">
                <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${toneMap[tone]}`}>
                    <Icon className="h-4 w-4" />
                </span>
                <TrendingUp className="h-3.5 w-3.5 text-success" />
            </div>
            <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
                <p className="font-serif text-3xl leading-tight mt-1">{value}</p>
                <p className="text-xs text-muted-foreground mt-1">{sub}</p>
            </div>
        </Card>
    );
}
